import { Component, OnInit, signal, computed, ChangeDetectorRef } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { IngestionPoint } from "../data-ingestion/data.ingestion.service";
import { S25RuleTreeModule } from "../s25-rule-tree/s25.rule.tree.module";
import { S25ButtonComponent } from "../../standalone/s25.button.component";
import { S25DropdownPaginatedModule } from "../s25-dropdown/s25.dropdown.paginated.module";
import { DropDownItem } from "../../pojo/DropDownItem";

@TypeManagerDecorator("workflow-init-rules")
@Component({
    selector: "workflow-init-rules",
    template: `
        <div class="workflow-init-rules">
            <div class="page-header">
                <h1>Workflow Initialization Rules</h1>
                <p class="page-description">
                    Configure rules to automatically assign workflow processes to form submissions based on form field
                    values.
                </p>
            </div>

            <div class="form-selector-section">
                <div class="form-group">
                    <label for="formSelector">Select Form:</label>
                    <div class="form-control-container">
                        @if (showFormDropdown()) {
                            <s25-ingestion-point-dropdown
                                [(chosen)]="selectedFormDropdownItem"
                                [enabledOnly]="true"
                                (chosenChange)="onFormSelected($event)"
                                class="form-dropdown"
                            ></s25-ingestion-point-dropdown>
                        }
                        <s25-ng-button
                            type="primary"
                            buttonText="Refresh"
                            [onClick]="refreshForms"
                            buttonClass="refresh-btn"
                        ></s25-ng-button>
                    </div>
                </div>
            </div>

            @if (selectedFormId) {
                <div class="rules-editor">
                    <div class="editor-header">
                        <h2>Workflow Rules for: {{ getSelectedFormName() }}</h2>
                        <p>Define rules to automatically assign workflows based on form field values.</p>
                    </div>

                    <div class="rule-tree-container">
                        <s25-ng-rule-tree
                            [category]="'workflowInit'"
                            [subCategory]="selectedFormId.toString()"
                            [conditionFilterMap]="workflowInitConditionFilterMap()"
                        ></s25-ng-rule-tree>
                    </div>
                </div>
            } @else {
                <div class="no-selection">
                    <div class="no-selection-content">
                        <h3>Select a Form</h3>
                        <p>Choose a form from the dropdown above to configure its workflow initialization rules.</p>
                        <div class="help-text">
                            <h4>How Workflow Initialization Works:</h4>
                            <ol>
                                <li><strong>Form Submission:</strong> User submits a form</li>
                                <li><strong>Rule Evaluation:</strong> System checks conditional rules</li>
                                <li><strong>Workflow Assignment:</strong> Assigns matching workflow process</li>
                                <li><strong>Process Start:</strong> Workflow begins with initial state and assignee</li>
                            </ol>
                        </div>
                    </div>
                </div>
            }
        </div>
    `,
    styles: `
        .workflow-init-rules {
            padding: 20px;
            max-width: 1000px;
            margin: 0 auto;
        }

        .page-header {
            margin-bottom: 24px;
            padding-bottom: 16px;
            border-bottom: 2px solid #dee2e6;
        }

        .page-header h1 {
            margin: 0 0 8px 0;
            color: #333;
        }

        .page-description {
            margin: 0;
            color: #666;
            font-size: 14px;
        }

        .form-selector-section {
            background: #f8f9fa;
            border: 1px solid #dee2e6;
            border-radius: 8px;
            padding: 20px;
            margin-bottom: 20px;
        }

        .form-group {
            margin-bottom: 16px;
        }

        .form-group label {
            display: block;
            margin-bottom: 8px;
            font-weight: 500;
            color: #555;
        }

        .form-control-container {
            display: flex;
            align-items: center;
            gap: 8px;
            max-width: 400px;
        }

        .form-control {
            flex: 1;
            padding: 10px 12px;
            border: 1px solid #ddd;
            border-radius: 4px;
            font-size: 14px;
            box-sizing: border-box;
        }

        .form-control:focus {
            outline: none;
            border-color: #007bff;
            box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
        }

        .refresh-btn {
            min-width: 40px;
            height: 40px;
        }

        .form-dropdown {
            flex: 1;
        }

        .refresh-btn i {
            font-size: 16px;
        }

        .rules-editor {
            background: white;
            border: 1px solid #dee2e6;
            border-radius: 8px;
            overflow: hidden;
        }

        .editor-header {
            background: #f8f9fa;
            padding: 20px;
            border-bottom: 1px solid #dee2e6;
        }

        .editor-header h2 {
            margin: 0 0 8px 0;
            color: #333;
        }

        .editor-header p {
            margin: 0 0 12px 0;
            color: #666;
            font-size: 14px;
        }

        .form-info {
            background: #e3f2fd;
            padding: 8px 12px;
            border-radius: 4px;
            font-size: 12px;
            color: #1565c0;
        }

        .rule-tree-container {
            padding: 20px;
        }

        .no-selection {
            text-align: center;
            padding: 60px 20px;
        }

        .no-selection-content h3 {
            color: #333;
            margin-bottom: 12px;
        }

        .no-selection-content p {
            color: #666;
            margin-bottom: 24px;
        }

        .help-text {
            background: #f8f9fa;
            border: 1px solid #dee2e6;
            border-radius: 8px;
            padding: 20px;
            text-align: left;
            max-width: 500px;
            margin: 0 auto;
        }

        .help-text h4 {
            color: #333;
            margin: 0 0 12px 0;
        }

        .help-text ol {
            margin: 0;
            color: #666;
        }

        .help-text li {
            margin-bottom: 8px;
        }
    `,
    standalone: true,
    imports: [CommonModule, FormsModule, S25RuleTreeModule, S25ButtonComponent, S25DropdownPaginatedModule],
})
export class WorkflowInitRulesComponent implements OnInit {
    selectedFormId: number | null = null;
    selectedFormDropdownItem: DropDownItem | null = null;
    showFormDropdown = signal(true);

    constructor(private cd: ChangeDetectorRef) {}

    // Condition filter map for workflowInit rules - uses ingestion fields for the selected form
    workflowInitConditionFilterMap = computed(() => {
        if (!this.selectedFormId) return {};
        return {
            ingestionFieldsAll: "" + this.selectedFormId,
        };
    });

    ngOnInit() {}

    refreshForms = async () => {
        // Destroy and recreate the dropdown by toggling the @if variable
        this.showFormDropdown.set(false);
        this.cd.detectChanges();
        this.showFormDropdown.set(true);
        this.cd.detectChanges();
    };

    onFormSelected(dropdownItem: DropDownItem | null): void {
        this.selectedFormDropdownItem = dropdownItem;
        this.selectedFormId = dropdownItem?.itemId ? Number(dropdownItem.itemId) : null;
    }

    getSelectedFormName(): string {
        if (!this.selectedFormId) return "";
        const form = this.selectedFormDropdownItem?.data as IngestionPoint;
        return form?.ingestionPointName || `Form ${this.selectedFormId}`;
    }
}
